import { buildNewGroup, familyAdapters } from '../domain/familyAdapters';
import { toId } from '../domain/groupIds';
import { applyGroupChange } from '../domain/groupOperations';
import { toVenueGroupsBuffer } from '../domain/venueGroupsPersistence';

import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { TemporalPacingInterface } from 'sonata-static-storage/lib/schemas/campaign/temporalPacing.schema';
import type { Context } from '../../multiFlightMap.types';
import type {
  GroupableItem,
  GroupChange,
  GroupChangeOutcome,
  GroupFamily,
  GroupingState,
  GroupLike,
  NewGroupBase
} from '../domain/grouping.types';

/** What happened (or would happen) when resolving a change: everything but the next state. */
export type GroupChangeSummary = Omit<GroupChangeOutcome, 'state'>;

/** Family-erased view over the map context: read the family data and resolve/commit changes. */
export interface FamilyBridge {
  groups: GroupLike[];
  items: GroupableItem[];
  run: (change: GroupChange, commit: boolean) => GroupChangeSummary;
}

interface BridgeConfig<G extends GroupLike, I extends GroupableItem> {
  buildGroup: (base: NewGroupBase) => G;
  commitGroups: (groups: G[]) => void;
  /** Receives only the items whose membership changed. */
  commitItems: (items: I[]) => void;
  context: Context;
  family: GroupFamily;
  groups: G[];
  items: I[];
}

function makeBridge<G extends GroupLike, I extends GroupableItem>(config: BridgeConfig<G, I>): FamilyBridge {
  const { context, family } = config;
  const state: GroupingState<G, I, AdInterface<string>, TemporalPacingInterface> = {
    ads: context.ads ?? [],
    groups: config.groups,
    items: config.items,
    temporalPacing: context.temporalPacing
  };
  const groupContext = {
    buildGroup: config.buildGroup,
    existedOnLoad: context.groupsExistingOnLoad[family],
    family,
    isDraft: context.entityIsDraft
  };

  const commit = ({
    affectedItemIds,
    state: next
  }: GroupChangeOutcome<G, I, AdInterface<string>, TemporalPacingInterface>) => {
    const affected = new Set(affectedItemIds);
    const { itemId } = familyAdapters[family];
    if (affected.size) {
      config.commitItems(next.items.filter(item => affected.has(itemId(item))));
    }
    if (next.groups !== state.groups) {
      config.commitGroups(next.groups);
    }
    if (next.ads.some((ad, index) => ad !== state.ads[index])) {
      context.onChangeAds?.(next.ads);
    }
    if (next.temporalPacing && next.temporalPacing !== state.temporalPacing) {
      context.onChangeTemporalPacing?.(next.temporalPacing);
    }
  };

  return {
    groups: state.groups,
    items: state.items,
    run: (change, shouldCommit) => {
      const outcome = applyGroupChange(state, change, groupContext);
      if (shouldCommit && outcome.state !== state) {
        commit(outcome);
      }
      const { state: _next, ...summary } = outcome;
      return summary;
    }
  };
}

const groupsById = <G extends GroupLike>(groups: G[]): Map<string, G> =>
  new Map(groups.map(group => [toId(group._id), group]));

/** Builds the bridge between the domain and the map context for one family. */
export function createFamilyBridge(family: GroupFamily, context: Context): FamilyBridge {
  switch (family) {
    case 'screens':
      return makeBridge({
        buildGroup: base => buildNewGroup('screens', base),
        commitGroups: groups => {
          const next = groupsById(groups);
          context.onChangeScreenGroups(groups);
          // Keep the legacy in-flight buffer consistent: it overrides the entity groups in the merged view.
          context.setTemporaryScreenGroups(prev => prev.flatMap(group => next.get(toId(group._id)) ?? []));
        },
        commitItems: changed => {
          const byId = new Map(changed.map(screen => [toId(screen.source_element_id), screen]));
          context.updateScreens(prev =>
            prev.map(screen => {
              const update = byId.get(toId(screen.source_element_id));
              return update ? { ...screen, groups: update.groups, isModified: true } : screen;
            })
          );
        },
        context,
        family,
        groups: context.screenGroups,
        items: context.temporaryScreens
      });
    case 'venues':
      return makeBridge({
        buildGroup: base => buildNewGroup('venues', base),
        commitGroups: groups =>
          context.setTemporaryVenueGroups(toVenueGroupsBuffer(context.user.venueGroups ?? [], groups)),
        commitItems: changed => context.updateVenue(changed.map(({ _id, groups }) => ({ _id, groups }))),
        context,
        family,
        groups: context.venueGroups,
        items: context.temporaryVenues
      });
    default:
      return makeBridge({
        buildGroup: base => buildNewGroup('locations', base),
        commitGroups: context.onChangeZoneGroups,
        commitItems: changed => context.updateZone(changed.map(({ _id, groups }) => ({ _id, groups }))),
        context,
        family,
        groups: context.zoneGroups,
        items: context.temporaryZones
      });
  }
}
